"use client";

import { usePostHog } from "posthog-js/react";
import { useEffect, useId, useRef, useState } from "react";
import { APPLY_CLICKED_EVENT } from "@/components/monetization/post-apply-offer";
import { trackJobInteraction } from "@/lib/analytics/track";
import {
	claimConfirmForJob,
	isConfirmSuppressedForJob,
	onConfirmSuppressed,
	releaseConfirmForJob,
	suppressConfirmForJob,
} from "@/lib/apply/confirm-suppression";

/**
 * The "did you apply?" ask, rendered IN the job page at apply-click time.
 *
 * Why this exists next to `ConfirmApplyToast` rather than replacing it: the
 * toast is triggered by `visibilitychange`, so it only ever reaches a visitor
 * who comes BACK to our tab. Measured over the 7 days to 2026-09-07 that was
 * 54 impressions across 19 distinct actors — precisely the
 * `guest_apply_hops.had_prior_cookie = 1` cohort, i.e. it already reaches
 * essentially every returning browser and cannot reach anyone else. 6 answered.
 * The email step sits two answers deeper (`applied` AND `firstApply`), which is
 * why `contact_email` was NULL on 8,057 of 8,057 hops despite its flag being
 * live at 100% for 35 days.
 *
 * `AfterApplyInline` already made exactly this move for the monetization unit
 * and says so in its own docstring — the offer got the reliable trigger and the
 * ask never did. This closes that gap: same `ld:apply-clicked` hook, same
 * events, different surface value.
 *
 * The toast is NOT removed — it still covers feed and hunt hops, which never
 * pass through this component. The two are kept from double-asking by the
 * cross-surface suppression protocol (`@/lib/apply/confirm-suppression`), not
 * by either one knowing about the other.
 *
 * `enabled` is resolved SERVER-side by the page and passed down, deliberately.
 * A client `useExperiment` read would start on its control default and only
 * leave it inside PostHog's `onFeatureFlags` callback, so a visitor whose flags
 * never arrive — consent not yet granted, an ad-blocker, a slow `/decide` —
 * would silently get the "off" branch. That is the exact shape of the defect
 * #5113 fixed one commit ago; it must not be reintroduced by the PR that
 * benefits from having fixed it.
 *
 * ARM, DON'T SHOW, ON CLICK (2026-09-28). Showing the prompt synchronously on
 * `ld:apply-clicked` asked the visitor while they were still on the
 * employer's tab (external applies open `target=_blank`) — PostHog 14d: 148
 * shown, 5 answered (3%). The click now only arms a pending confirm; the
 * prompt reveals when the tab goes hidden and then visible again, at least
 * `INPAGE_CONFIRM_MIN_AWAY_MS` after the click. An apply that never leaves
 * the tab (internal flow) never goes hidden, so it never shows.
 */

/** Minimum time away from the tab (hidden → visible) before the prompt may
 * reveal, measured from the `ld:apply-clicked` moment. Exported for tests. */
export const INPAGE_CONFIRM_MIN_AWAY_MS = 30_000;

/** `answer("not_applied")`'s optional follow-up reason chip. */
export type NotAppliedReason = "later" | "need_cv" | "not_fit" | "link_broken";

type Status =
	| "idle"
	| "asking"
	| "not_applied_reason"
	| "submitting"
	| "email"
	| "saved";

type Copy = {
	prompt: (company: string | null) => string;
	applied: string;
	notYet: string;
	dismiss: string;
	reasonSkip: string;
	reasons: Record<NotAppliedReason, string>;
	emailHeading: string;
	emailBody: string;
	emailLabel: string;
	emailPlaceholder: string;
	emailCta: string;
	emailSkip: string;
	saved: string;
	logged: string;
};

const EN: Copy = {
	prompt: (company) =>
		company ? `Did you apply to ${company}?` : "Did you apply to this job?",
	applied: "Yes, I applied",
	notYet: "Not yet",
	dismiss: "Dismiss",
	reasonSkip: "Skip",
	reasons: {
		later: "Later — the form is long",
		need_cv: "I need an English CV",
		not_fit: "Not a fit after all",
		link_broken: "The apply link is broken",
	},
	emailHeading: "Want a reminder to follow up?",
	emailBody:
		"We'll email you once to nudge you to follow up on this application. No account needed.",
	emailLabel: "Email address",
	emailPlaceholder: "you@example.com",
	emailCta: "Remind me",
	emailSkip: "No thanks",
	saved: "Done — we'll remind you.",
	logged: "Application logged",
};

const ID: Copy = {
	prompt: (company) =>
		company ? `Sudah lamar ke ${company}?` : "Sudah lamar lowongan ini?",
	applied: "Sudah",
	notYet: "Belum",
	dismiss: "Abaikan",
	reasonSkip: "Lewati",
	reasons: {
		later: "Nanti saja, formnya panjang",
		need_cv: "Perlu CV bahasa Inggris",
		not_fit: "Ternyata tidak cocok",
		link_broken: "Link lamarannya bermasalah",
	},
	emailHeading: "Mau kami ingatkan buat follow up?",
	emailBody:
		"Kami kirim satu email pengingat buat follow up lamaran ini. Tanpa perlu buat akun.",
	emailLabel: "Alamat email",
	emailPlaceholder: "kamu@contoh.com",
	emailCta: "Ingatkan saya",
	emailSkip: "Tidak, terima kasih",
	saved: "Oke — nanti kami ingatkan.",
	logged: "Lamaran tercatat",
};

const BTN =
	"min-h-11 rounded-lg border border-border bg-card px-3 py-1.5 text-xs font-semibold text-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50";

const CHIP =
	"min-h-11 rounded-full border border-border bg-card px-2.5 py-1 text-xs text-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background";

/** Send the "Not yet" answer at most once — the chip tap, the skip tap, the
 * no-reason timeout, and an unmount can all reach here for the same job. */
const NOT_APPLIED_REASON_TIMEOUT_MS = 15_000;

/**
 * The job page mounts two `JobApplyCta` (desktop sidebar + mobile footer), each
 * with its own `InPageApplyConfirm`, and both hear the one `ld:apply-clicked`
 * window event — so a single click used to emit two impressions, doubling the
 * denominator of every confirm-rate read (48h reality check F5). `shownRef`
 * only dedupes within an instance; this dedupes across them. Two seconds is far
 * longer than one event dispatch and far shorter than a real second view.
 */
const SHOWN_DEDUPE_MS = 2000;
const lastShownAt = new Map<string, number>();

/** Exported for tests, like `resetApplyReadinessCache`: the map outlives a test. */
export function resetInPageConfirmShownDedupe(): void {
	lastShownAt.clear();
}

export function InPageApplyConfirm({
	jobId,
	company,
	locale,
	enabled,
}: {
	jobId: string;
	company: string | null;
	locale: string;
	enabled: boolean;
}) {
	const posthog = usePostHog();
	const surfaceId = useId();
	const t = locale === "en" ? EN : ID;

	const [status, setStatus] = useState<Status>("idle");
	const [email, setEmail] = useState("");
	const [logged, setLogged] = useState(false);
	const shownRef = useRef(false);
	// Set on `ld:apply-clicked`, cleared once the prompt reveals (or the arm
	// expires unused, e.g. a second click). Not state: arming must not itself
	// trigger a render, only the eventual reveal does.
	const armedAtRef = useRef<number | null>(null);
	// True once the tab has gone hidden while armed — the reveal-on-return
	// check below only fires on the hidden -> visible edge, never on a bare
	// "visible" (e.g. the initial mount fires no visibilitychange at all).
	const wentHiddenRef = useRef(false);
	// "Not yet" -> reason-chips bookkeeping (see `openNotYetReason` /
	// `sendNotApplied` below). `sendNotAppliedRef` always holds the current
	// render's `sendNotApplied` closure so the unmount cleanup effect (which
	// only runs once) never calls a stale one.
	const notAppliedSentRef = useRef(false);
	const notAppliedPendingRef = useRef(false);
	const reasonTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const sendNotAppliedRef = useRef<(reason?: NotAppliedReason) => void>(
		() => {},
	);
	// Read inside the always-on click listener without re-subscribing it.
	const enabledRef = useRef(enabled);
	enabledRef.current = enabled;

	// Always listen, even when disabled — the reveal is what `enabled` gates, so
	// flipping the flag on does not depend on a listener that was never attached
	// (the same "always listen, gate the reveal" shape AfterApplyInline uses).
	// This only ARMS the prompt — see the reveal effect below for why.
	useEffect(() => {
		const onApply = (event: Event) => {
			const detail = (event as CustomEvent<{ jobId?: string }>).detail;
			if (detail?.jobId !== jobId) return;
			if (isConfirmSuppressedForJob(jobId)) return;
			// One prompt per job (confirm-suppression.ts → claims). While armed
			// AND enabled, this ask holds the job's slot so the tab-return toast
			// stays hidden; if the toast is already on screen, do not arm at all.
			// A disabled (flag off) ask never claims — the toast owns the job.
			if (enabledRef.current && !claimConfirmForJob(jobId, "inpage")) return;
			armedAtRef.current = Date.now();
			wentHiddenRef.current = false;
		};
		window.addEventListener(APPLY_CLICKED_EVENT, onApply);
		return () => {
			window.removeEventListener(APPLY_CLICKED_EVENT, onApply);
			releaseConfirmForJob(jobId, "inpage");
		};
	}, [jobId]);

	// Reveal only on a hidden -> visible round trip, at least
	// `INPAGE_CONFIRM_MIN_AWAY_MS` after the click. An apply that never leaves
	// this tab (an internal/same-tab flow) never fires `visibilitychange` at
	// all, so it never reveals — which is the point: this surface exists for
	// the external, `target=_blank` apply hop.
	useEffect(() => {
		const onVisibility = () => {
			if (armedAtRef.current === null) return;
			if (document.visibilityState === "hidden") {
				wentHiddenRef.current = true;
				return;
			}
			if (document.visibilityState !== "visible" || !wentHiddenRef.current) {
				return;
			}
			const armedAt = armedAtRef.current;
			armedAtRef.current = null;
			wentHiddenRef.current = false;
			if (
				!enabledRef.current ||
				Date.now() - armedAt < INPAGE_CONFIRM_MIN_AWAY_MS ||
				isConfirmSuppressedForJob(jobId)
			) {
				// The arm expired without a reveal — hand the slot back so the
				// toast can still ask on a later return.
				releaseConfirmForJob(jobId, "inpage");
				return;
			}
			setStatus((prev) => (prev === "idle" ? "asking" : prev));
		};
		document.addEventListener("visibilitychange", onVisibility);
		return () => document.removeEventListener("visibilitychange", onVisibility);
	}, [jobId]);

	// Another surface answered for this job — stand down. Passing `surfaceId`
	// means our OWN answer never hides us mid-flow, which is what keeps the
	// email step reachable after "Sudah". A pending "not yet" reason-chip send
	// is cancelled outright rather than sent: the job already got a terminal
	// answer from the sibling surface, so ours would be a stale duplicate.
	useEffect(() => {
		return onConfirmSuppressed(
			jobId,
			() => {
				notAppliedPendingRef.current = false;
				if (reasonTimerRef.current) {
					clearTimeout(reasonTimerRef.current);
					reasonTimerRef.current = null;
				}
				setStatus("idle");
			},
			surfaceId,
		);
	}, [jobId, surfaceId]);

	// Hand the job's slot back once this ask is done (answered, dismissed, or
	// stood down) — the answer itself suppresses the toast for this job.
	const isIdle = status === "idle";
	useEffect(() => {
		if (isIdle) return;
		return () => releaseConfirmForJob(jobId, "inpage");
	}, [isIdle, jobId]);

	// Send the "not yet" answer with no reason if the visitor navigates away
	// (or the job page unmounts this instance) while the reason chips are
	// still showing — never dropped, never double-posted.
	useEffect(() => {
		return () => {
			if (reasonTimerRef.current) clearTimeout(reasonTimerRef.current);
			if (notAppliedPendingRef.current) sendNotAppliedRef.current();
		};
	}, []);

	// One impression per mount, on the same two channels the toast uses so the
	// pair stays comparable — only `surface` differs.
	useEffect(() => {
		if (!enabled || status !== "asking" || shownRef.current) return;
		shownRef.current = true;
		const now = Date.now();
		if (now - (lastShownAt.get(jobId) ?? 0) < SHOWN_DEDUPE_MS) return;
		lastShownAt.set(jobId, now);
		posthog?.capture("apply_confirm_prompt_shown", {
			job_id: jobId,
			surface: "job_detail_inpage",
			locale: locale === "en" ? "en" : "id",
		});
		trackJobInteraction({
			jobId,
			interactionType: "confirm_prompt_shown",
			surface: "job_detail_inpage",
			locale: locale === "en" ? "en" : "id",
		});
	}, [enabled, status, jobId, locale, posthog]);

	if (!enabled || status === "idle") return null;

	const answer = async (value: "applied" | "dismiss") => {
		// "Dismiss" is deliberately client-only: it isn't an answer worth a row,
		// and writing one would put a non-answer into the same column a real
		// confirmation lives in. "Not yet" goes through `openNotYetReason` /
		// `sendNotApplied` below instead of this function.
		if (value === "dismiss") {
			suppressConfirmForJob(jobId, surfaceId);
			setStatus("idle");
			return;
		}
		setStatus("submitting");
		let requiresSignupToKeep = false;
		try {
			const res = await fetch("/api/apply/confirm", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({
					jobId,
					answer: value,
					surface: "job_detail_inpage",
				}),
			});
			const body = (await res.json().catch(() => null)) as {
				requiresSignupToKeep?: boolean;
			} | null;
			requiresSignupToKeep = body?.requiresSignupToKeep === true;
		} catch {
			// Best-effort: a failed confirm must never cost the visitor their
			// apply. The write is recoverable (the toast can still ask on a later
			// visit); blocking the UI here is not.
		}
		suppressConfirmForJob(jobId, surfaceId);
		setLogged(true);
		// Only a guest gets the email step — `/api/apply/track-email` refuses
		// an authenticated caller with `not_applicable`, and a signed-in
		// applicant already has an address we can reach.
		if (requiresSignupToKeep) {
			setStatus("email");
			return;
		}
		setStatus("idle");
	};

	// "Not yet" is a real answer (`not_applied`) with an optional reason chip
	// attached — the answer itself is worth learning from even without a
	// reason, so suppress sibling surfaces the moment the visitor taps it,
	// before the reason (or skip, or timeout) resolves the single POST below.
	const openNotYetReason = () => {
		suppressConfirmForJob(jobId, surfaceId);
		notAppliedPendingRef.current = true;
		setStatus("not_applied_reason");
		reasonTimerRef.current = setTimeout(() => {
			sendNotAppliedRef.current();
		}, NOT_APPLIED_REASON_TIMEOUT_MS);
	};

	// Sends the "not yet" answer at most once (guarded by `notAppliedSentRef`)
	// — reachable from a chip tap, "Lewati"/"Skip", the 15s no-reason timeout,
	// or an unmount while the chips are showing.
	const sendNotApplied = (reason?: NotAppliedReason) => {
		if (notAppliedSentRef.current) return;
		notAppliedSentRef.current = true;
		notAppliedPendingRef.current = false;
		if (reasonTimerRef.current) {
			clearTimeout(reasonTimerRef.current);
			reasonTimerRef.current = null;
		}
		const payload: {
			jobId: string;
			answer: "not_applied";
			surface: "job_detail_inpage";
			reason?: NotAppliedReason;
		} = { jobId, answer: "not_applied", surface: "job_detail_inpage" };
		if (reason) payload.reason = reason;
		fetch("/api/apply/confirm", {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify(payload),
		}).catch(() => {
			// Best-effort — same reasoning as the "applied" write above.
		});
		setStatus("idle");
	};
	sendNotAppliedRef.current = sendNotApplied;

	const submitEmail = async () => {
		const trimmed = email.trim();
		if (trimmed.length === 0) return;
		setStatus("submitting");
		try {
			await fetch("/api/apply/track-email", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ jobId, email: trimmed }),
			});
		} catch {
			// Same reasoning as the confirm above.
		}
		setStatus("saved");
	};

	return (
		<div
			data-testid="inpage-apply-confirm"
			data-status={status}
			className="rounded-lg border border-border bg-muted/40 p-3"
		>
			{status === "email" || status === "saved" ? (
				<div className="space-y-2">
					{logged ? (
						<p className="text-xs font-semibold text-foreground">{t.logged}</p>
					) : null}
					{status === "saved" ? (
						<p className="text-xs text-muted-foreground">{t.saved}</p>
					) : (
						<>
							<p className="text-xs font-semibold text-foreground">
								{t.emailHeading}
							</p>
							<p className="text-xs text-muted-foreground">{t.emailBody}</p>
							<div className="flex gap-2">
								<label className="sr-only" htmlFor={`${surfaceId}-email`}>
									{t.emailLabel}
								</label>
								<input
									id={`${surfaceId}-email`}
									type="email"
									inputMode="email"
									autoComplete="email"
									value={email}
									onChange={(e) => setEmail(e.target.value)}
									placeholder={t.emailPlaceholder}
									className="min-w-0 flex-1 rounded-lg border border-input bg-background px-2.5 py-1.5 text-xs text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
								/>
								<button type="button" className={BTN} onClick={submitEmail}>
									{t.emailCta}
								</button>
							</div>
							<button
								type="button"
								className="text-xs text-muted-foreground underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
								onClick={() => setStatus("idle")}
							>
								{t.emailSkip}
							</button>
						</>
					)}
				</div>
			) : status === "not_applied_reason" ? (
				<div className="space-y-2">
					<div className="flex flex-wrap gap-2">
						{(Object.keys(t.reasons) as NotAppliedReason[]).map((reasonId) => (
							<button
								key={reasonId}
								type="button"
								className={CHIP}
								onClick={() => sendNotApplied(reasonId)}
							>
								{t.reasons[reasonId]}
							</button>
						))}
						<button
							type="button"
							className="text-xs text-muted-foreground underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
							onClick={() => sendNotApplied()}
						>
							{t.reasonSkip}
						</button>
					</div>
				</div>
			) : (
				<div className="space-y-2">
					<p className="text-xs font-semibold text-foreground">
						{t.prompt(company)}
					</p>
					<div className="flex flex-wrap gap-2">
						<button
							type="button"
							className={BTN}
							disabled={status === "submitting"}
							onClick={() => answer("applied")}
						>
							{t.applied}
						</button>
						<button
							type="button"
							className={BTN}
							disabled={status === "submitting"}
							onClick={openNotYetReason}
						>
							{t.notYet}
						</button>
						<button
							type="button"
							className="text-xs text-muted-foreground underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
							disabled={status === "submitting"}
							onClick={() => answer("dismiss")}
						>
							{t.dismiss}
						</button>
					</div>
				</div>
			)}
		</div>
	);
}
